/**
 * PanelChannelBoards — 채팅 → 게시판 모듈 연동 패널
 *
 * 현재 Room(채널)에 연결된 게시판 목록을 표시하고,
 * 클릭 시 게시판 모듈로 전환하여 해당 게시판을 선택
 */
import React, { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { FileText, Bell, MessageSquare, BookOpen, ListTodo, ExternalLink, LayoutList } from 'lucide-react';
import { useAppUIContext } from '../../../context/AppUIContext';
import { useBoardContext } from '../../../context/BoardContext';
import { Board, BoardType } from '../../../models/Board';
import { fetchBoards } from '../../../services/BoardService';
import { SectionCard, SectionListItem, SectionActionButton } from './SectionCard';
import { useHistory } from '../../../hooks/useRouterCompat';
import { cn } from '../../../lib/utils';
import { buildBoardUrl } from '../../board/boardUrl';

/** 게시판 유형별 아이콘 */
function boardIcon(type: BoardType) {
  switch (type) {
    case 'notice': return Bell;
    case 'qna': return MessageSquare;
    case 'wiki': return BookOpen;
    case 'issue': return ListTodo;
    default: return FileText;
  }
}

/** 게시판 유형 라벨 */
function typeLabel(type: BoardType) {
  switch (type) {
    case 'notice': return t`Announcement`;
    case 'qna': return 'Q&A';
    case 'wiki': return t`Wiki`;
    case 'issue': return t`Issue`;
    default: return t`General`;
  }
}

interface PanelChannelBoardsProps {
  roomId: string;
}

export const PanelChannelBoards: React.FC<PanelChannelBoardsProps> = ({ roomId }) => {
  const { activeWorkspaceId, setActiveModule } = useAppUIContext();
  const boardCtx = useBoardContext();
  const history = useHistory();
  const [channelBoards, setChannelBoards] = useState<Board[]>([]);

  useEffect(() => {
    if (!activeWorkspaceId) return;

    // 워크스페이스의 전체 게시판에서 현재 채널에 연결된 것만 필터
    fetchBoards(activeWorkspaceId)
      .then(boards => {
        const filtered = boards.filter(
          b => b.scope === 'channel' && b.channel_id === roomId
        );
        setChannelBoards(filtered);
      })
      .catch(err => console.error('[PanelChannelBoards] 로드 실패:', err));
  }, [activeWorkspaceId, roomId]);

  // 연결된 게시판이 없으면 렌더링하지 않음
  if (channelBoards.length === 0) return null;

  const handleBoardClick = (board: Board) => {
    // 1. 게시판 모듈로 전환
    setActiveModule('board');
    // 2. 해당 게시판 선택
    boardCtx.setSelectedBoard(board);
    // 3. URL 네비게이션
    history.push(buildBoardUrl(board.id));
  };

  return (
    <SectionCard
      title={t`Boards`}
      count={channelBoards.length}
      collapsible={true}
      defaultExpanded={true}
      icon={<LayoutList className="size-4 text-muted-foreground" />}
    >
      <div className="flex flex-col gap-0.5">
        {channelBoards.map(board => {
          const Icon = boardIcon(board.type as BoardType);
          return (
            <button
              key={board.id}
              onClick={() => handleBoardClick(board)}
              className={cn(
                'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-label',
                'text-foreground/80 hover:bg-muted/60 hover:text-foreground',
                'transition-all duration-150 cursor-pointer group',
              )}
            >
              <Icon className="size-4 text-muted-foreground shrink-0" />
              <span className="truncate flex-1 text-left">{board.name}</span>

              {/* 유형 뱃지 */}
              <span className="text-meta-sm px-1.5 py-px rounded-full font-medium bg-muted text-muted-foreground shrink-0">
                {typeLabel(board.type as BoardType)}
              </span>

              {/* 게시글 수 */}
              {board.post_count > 0 && (
                <span className="text-meta-sm tabular-nums text-muted-foreground/60 shrink-0">
                  {board.post_count}
                </span>
              )}

              {/* 이동 아이콘 */}
              <ExternalLink className="size-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
};

export default PanelChannelBoards;
